import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import i18n from '@risksmart-app/i18n/src/i18n';
import {
  useGetControlsRegister,
  type UseGetControlsRegisterArgs,
} from 'src/hooks/queries/control/useGetControlsRegister';
import type { ControlTableFields } from 'src/pages/controls/types';

import { controlRegisterUrl } from '@/utils/urls';

import { useGetControlSmartWidgetTableProps } from '../../../controls/config';
import { UNRATED } from '../../gigawidget/types';
import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  dateRangeClickthroughFilter,
  defaultClickthroughFilter,
  defaultClickthroughFilterWithUnrated,
  tagAndDepartmentCategoryGetters,
} from '../dataSourceHelpers';

type GetControlsData = NonNullable<
  ReturnType<typeof useGetControlsRegister>['data']
>;

export default createDataSource<
  ControlTableFields,
  UseGetControlsRegisterArgs,
  GetControlsData
>({
  group: 'single',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Control],
  useWidgetQuery: useGetControlsRegister,
  useTablePropsHook: (data, options) =>
    useGetControlSmartWidgetTableProps(data?.control, options),
  entityNamePlural: 'control_other',
  entityNameSingular: 'control_one',
  fields: 'controls.fields',
  dashboardFilterConfig: {
    tagsFilter: (tags) => ({ tagTypeIds: tags }),
    departmentsFilter: (departments) => ({ departmentTypeIds: departments }),
    dateFilter: (dateRange, precision) => ({
      dateRange: {
        field: 'CreatedAtTimestamp',
        ...toIsoBounds(dateRange, precision),
      },
    }),
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
    ownershipFilter: (myItemsFilters) => ({ ownership: myItemsFilters }),
  },
  clickThroughUrl: (filters) => controlRegisterUrl(filters),
  categoryGetters: [
    ...tagAndDepartmentCategoryGetters<ControlTableFields>(),
    {
      id: 'effectiveness',
      name: () => i18n.t('controls.columns.effectiveness'),
      categoryGetter: (item) => {
        if (item.OverallEffectiveness === null) {
          return { key: UNRATED, label: UNRATED };
        }

        return {
          key: item.OverallEffectiveness,
          label: item.OverallEffectivenessLabelled,
        };
      },
      clickthroughFilter: defaultClickthroughFilter(
        'OverallEffectivenessLabelled'
      ),
      ratingColourKey: 'effectiveness',
    },
    {
      id: 'oversightEffectiveness',
      name: () => i18n.t('controls.columns.oversight_overall_effectiveness'),
      // Latest oversight effectiveness, merged onto the row by the register
      // table-props hook (gated), so absent → Unrated without oversight access.
      categoryGetter: (item) => {
        if (item.OversightOverallEffectiveness == null) {
          return { key: UNRATED, label: UNRATED };
        }

        return {
          key: item.OversightOverallEffectiveness,
          label: item.OversightOverallEffectivenessLabelled ?? UNRATED,
        };
      },
      clickthroughFilter: defaultClickthroughFilterWithUnrated(
        'OversightOverallEffectivenessLabelled'
      ),
      ratingColourKey: 'effectiveness',
    },
    {
      id: 'type',
      fieldId: 'Type',
      name: () => i18n.t('controls.columns.type'),
      categoryGetter: (item) => ({
        key: item.Type ?? UNRATED,
        label: item.Type ? item.ControlTypeLabelled : 'No Type',
      }),
      clickthroughFilter: defaultClickthroughFilterWithUnrated(
        'ControlTypeLabelled'
      ),
    },
    {
      id: 'testFrequency',
      name: () => i18n.t('controls.columns.test_frequency'),
      categoryGetter: (item) => item.TestFrequency ?? UNRATED,
      clickthroughFilter: defaultClickthroughFilterWithUnrated('TestFrequency'),
    },
    {
      id: 'owner',
      fieldId: 'Owners',
      name: () => i18n.t('controls.columns.owner'),
      categoryGetter: (item) =>
        item.allOwners.map((owner) => ({ key: owner.id, label: owner.label })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'contributor',
      fieldId: 'Contributors',
      name: () => i18n.t('controls.columns.contributor'),
      categoryGetter: (item) =>
        item.allContributors.map((contributor) => ({
          key: contributor.id,
          label: contributor.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allContributors',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'testScheduleStatus',
      name: () => i18n.t('controls.columns.testScheduleStatus'),
      categoryGetter: (item) => {
        return item.TestScheduleStatus === '-'
          ? { key: '-', label: i18n.t('columns.unscheduled') }
          : item.TestScheduleStatusLabelled;
      },
      clickthroughFilter: (category) => [
        {
          propertyKey: 'TestScheduleStatusLabelled',
          operator: '=',
          value: category.key === '-' ? '-' : category.key,
        },
      ],
    },
    {
      id: 'createdDate',
      name: () => i18n.t('columns.created_on'),
      categoryGetter: (data) => new Date(data.CreatedAtTimestamp),
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'CreatedAtTimestamp',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('CreatedAtTimestamp'),
    },
    {
      id: 'nextTestDate',
      name: () => i18n.t('controls.columns.next_test_date'),
      categoryGetter: (data) =>
        data.NextTestDate ? new Date(data.NextTestDate) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'NextTestDate',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('NextTestDate'),
    },
    {
      id: 'nextTestOverdueDate',
      name: () => i18n.t('controls.columns.nextTestOverdue'),
      categoryGetter: (data) =>
        data.NextTestOverdueDate ? new Date(data.NextTestOverdueDate) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'NextTestOverdueDate',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('NextTestOverdueDate'),
    },
    {
      id: 'effectivenessTrend',
      name: () => i18n.t('controls.columns.effectiveness_trend'),
      categoryGetter: (item) => ({
        key: item.OverallEffectivenessTrend ?? UNRATED,
        label: item.OverallEffectivenessTrendLabelled ?? UNRATED,
      }),
      ratingColourKey: 'effectiveness_trend',
      clickthroughFilter: defaultClickthroughFilter(
        'OverallEffectivenessTrendLabelled',
        { unratedValue: UNRATED }
      ),
    },
  ],
});
